import { useFilters } from "../context/FilterContext";
import { Filter, Calendar, Layers, Users, Landmark, X, ChevronDown, Search, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { ALL_YEARS_OPTION, getDefaultFySelection, getFyButtonLabel, isAllFySelected, normalizeFySelection } from "../utils/dashboardFilters";
import { displayTheme } from "../utils/format";

const DEFAULT_FY = getDefaultFySelection();

const toTitleCase = (s) => s?.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ") || s;

function SearchableSelect({ value, onChange, options, placeholder = "All Grantees" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) { setQuery(""); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));
  const displayLabel = value ? toTitleCase(value) : placeholder;

  return (
    <div className="relative" ref={ref} data-testid="filter-grantee">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 text-[11px] border rounded px-1.5 py-0.5 bg-white transition-colors min-w-[120px] ${
          value ? "border-[#006837] text-[#006837]" : "border-slate-200 text-slate-700 hover:border-slate-300"
        }`}
      >
        <span className="flex-1 text-left truncate">{displayLabel}</span>
        <ChevronDown size={10} className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl" style={{ width: 280 }}>
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded border border-slate-200 focus-within:border-[#006837] focus-within:ring-1 focus-within:ring-red-100">
              <Search size={11} className="text-slate-600 flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search grantees..."
                className="flex-1 bg-transparent text-[11px] text-slate-700 placeholder-slate-400 outline-none"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-slate-500 hover:text-slate-500">
                  <X size={10} />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 240 }}>
            <button
              onClick={() => { onChange(""); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50 transition-colors ${!value ? "bg-blue-50 text-[#006837] font-medium" : "text-slate-700"}`}
            >
              {placeholder}
            </button>
            {filtered.length === 0 && <div className="px-3 py-3 text-[11px] text-slate-600 text-center">No grantees found</div>}
            {filtered.map((o) => (
              <button
                key={o}
                onClick={() => { onChange(o); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50 transition-colors ${value === o ? "bg-blue-50 text-[#006837] font-medium" : "text-slate-700"}`}
              >
                {toTitleCase(o)}
              </button>
            ))}
          </div>

          <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-600">{filtered.length} of {options.length} grantees</div>
        </div>
      )}
    </div>
  );
}

function FyMultiSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const normalizedValue = normalizeFySelection(value, options);
  const allSelected = isAllFySelected(normalizedValue, options);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleAllYears = () => {
    onChange(allSelected ? [] : [...options]);
  };

  const toggleYear = (fy) => {
    if (allSelected) {
      onChange([fy]);
      return;
    }
    const exists = normalizedValue.includes(fy);
    if (exists) {
      const next = normalizedValue.filter((item) => item !== fy);
      onChange(next);
      return;
    }
    onChange([...normalizedValue, fy]);
  };

  return (
    <div className="relative" ref={ref} data-testid="filter-fy">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none hover:border-slate-300 transition-colors min-w-[118px]"
      >
        <span className="flex-1 text-left truncate">{getFyButtonLabel(normalizedValue, options)}</span>
        <ChevronDown size={10} className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[220px] p-1.5">
          {[ALL_YEARS_OPTION, ...options].map((option) => {
            const checked = option === ALL_YEARS_OPTION ? allSelected : normalizedValue.includes(option);
            const onOptionClick = () => {
              if (option === ALL_YEARS_OPTION) toggleAllYears();
              else toggleYear(option);
            };

            return (
              <button
                key={option}
                onClick={onOptionClick}
                className={`w-full flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-[11px] text-left transition-colors ${checked ? "bg-red-50 text-[#006837]" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <span>{option}</span>
                <span className={`inline-flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-[#006837] bg-[#006837] text-white" : "border-slate-300 bg-white text-transparent"}`}>
                  <Check size={10} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function GlobalFilters() {
  const { filters, setFilters, filterOptions, viewMode, setViewMode } = useFilters();
  const fyOptions = filterOptions.fy_options || [];
  const allYearsSelected = isAllFySelected(filters.fy, fyOptions);
  const normalizedFy = normalizeFySelection(filters.fy, fyOptions);
  const fyDiffersFromDefault = normalizedFy.length !== DEFAULT_FY.length || !DEFAULT_FY.every(f => normalizedFy.includes(f));
  const hasActiveFilters = fyDiffersFromDefault || filters.theme || filters.grantee || filters.trust || filters.status;

  // Auto-reset view mode to "active" when All Years is selected
  useEffect(() => {
    if (allYearsSelected && viewMode !== "active") {
      setViewMode("active");
    }
  }, [allYearsSelected, viewMode, setViewMode]);

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-1.5 flex items-center gap-2 flex-wrap min-w-0" data-testid="global-filters">
      <div className={`flex items-center bg-slate-100 rounded p-0.5 mr-2 flex-shrink-0 ${allYearsSelected ? "opacity-40 pointer-events-none" : ""}`} data-testid="view-mode-toggle" title={allYearsSelected ? "View mode is disabled when All Years is selected" : ""}>
        {[
          ["active", "All Grants", "Show all grants active in any selected FY"],
          ["snapshot", "New", "Show grants approved in any selected FY"],
          ["ongoing", "Ongoing", "Show active grants excluding those approved in the selected FY set"],
        ].map(([mode, label, title]) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            disabled={allYearsSelected}
            className={`px-2 py-0.5 text-[11px] font-medium rounded transition-all ${viewMode === mode ? "bg-white shadow-sm text-[#006837]" : "text-slate-500 hover:text-slate-700"} ${allYearsSelected ? "cursor-not-allowed" : ""}`}
            title={allYearsSelected ? "View mode is disabled when All Years is selected" : title}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="w-px h-4 bg-slate-200 mx-1" />

      <div className="flex items-center gap-1 text-slate-600 text-[10px] font-medium uppercase">
        <Filter size={10} />
        <span>Filters</span>
      </div>

      <div className="flex items-center bg-slate-100 rounded-full p-0.5 gap-0.5 flex-shrink-0" data-testid="filter-status">
        {[["", "All"], ["Active", "Active"], ["Closed", "Closed"]].map(([val, label]) => (
          <button key={val}
            onClick={() => setFilters((f) => ({ ...f, status: val }))}
            data-testid={`status-pill-${label.toLowerCase()}`}
            className={`text-[11px] px-2 py-0.5 rounded-full font-medium transition-all ${filters.status === val ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <Calendar size={11} className="text-slate-600" />
        <FyMultiSelect
          value={filters.fy}
          options={fyOptions}
          onChange={(nextFy) => setFilters((f) => ({ ...f, fy: normalizeFySelection(nextFy, fyOptions) }))}
        />
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <Layers size={11} className="text-slate-600" />
        <select data-testid="filter-theme" value={filters.theme}
          onChange={(e) => setFilters((f) => ({ ...f, theme: e.target.value }))}
          className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-200 focus:border-[#006837] hover:border-slate-300 transition-colors">
          <option value="">All Themes</option>
          {filterOptions.theme_options.map((t) => <option key={t} value={t}>{displayTheme(t)}</option>)}
        </select>
      </div>

      {filterOptions.trust_options?.length > 0 && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Landmark size={11} className="text-slate-600" />
          <select data-testid="filter-trust" value={filters.trust}
            onChange={(e) => setFilters((f) => ({ ...f, trust: e.target.value }))}
            className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-200 focus:border-[#006837] hover:border-slate-300 transition-colors">
            <option value="">All Funds</option>
            {filterOptions.trust_options.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}

      <div className="flex items-center gap-1 flex-shrink-0">
        <Users size={11} className="text-slate-600" />
        <SearchableSelect
          value={filters.grantee}
          onChange={(val) => setFilters((f) => ({ ...f, grantee: val }))}
          options={filterOptions.grantee_options || []}
          placeholder="All Grantees"
        />
        {hasActiveFilters && (
          <button data-testid="clear-filters-btn"
            onClick={() => setFilters({ fy: [...DEFAULT_FY], theme: "", grantee: "", trust: "", status: "" })}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-[#006837] transition-colors ml-1"
            title="Clear all filters">
            <X size={12} />
          </button>
        )}
      </div>
    </div>
  );
}
